/**
 * El traductor del chat, para las tres pantallas donde se habla con el huésped:
 * la bandeja de entrada, el chat de la reserva y el hilo de la OTA.
 *
 * Encendido hace dos cosas:
 *   · lo que ESCRIBE el huésped se trae al idioma del panel, solo;
 *   · al pulsar Enter, lo escrito NO sale todavía: primero se ve en qué idioma
 *     le va a llegar, y el segundo Enter lo manda ya traducido.
 *
 * El idioma de salida se puede cambiar a mano: el que detectamos es el punto de
 * partida, no una condena —a un sueco se le contesta en inglés si es donde los
 * dos se entienden— y lo elegido se recuerda POR CONVERSACIÓN.
 *
 * Va como mixin y no como componente porque las tres pantallas tienen su propia
 * lista de mensajes y su propia caja de escribir: lo común es la lógica, no el
 * HTML. La barra sí es un componente (BarraTraduccion.vue).
 */
import axios from "axios";

const CLAVE_ACTIVO = "wac_chat_traducir";
const CLAVE_SALIDA = "wac_chat_salida_";

/** Los idiomas que ofrece el selector de salida, además del del huésped. */
export const IDIOMAS_SALIDA = [
  "es", "en", "it", "fr", "de", "pt", "nl", "ca", "pl", "ro", "ru", "uk",
  "sv", "da", "no", "fi", "cs", "hu", "el", "tr", "ar", "he", "hi", "zh",
  "ja", "ko", "th", "vi", "id",
];

/** El nombre de cada idioma EN SU idioma: lo lee quien lo habla. */
export const NOMBRE_IDIOMA = {
  es: "español", en: "English", it: "italiano", fr: "français", de: "Deutsch",
  pt: "português", nl: "Nederlands", pl: "polski", ar: "العربية", ru: "русский",
  ro: "română", ca: "català", sv: "svenska", da: "dansk", no: "norsk",
  fi: "suomi", el: "ελληνικά", tr: "Türkçe", cs: "čeština", hu: "magyar",
  uk: "українська", zh: "中文（简体）", ja: "日本語", ko: "한국어",
  th: "ไทย", vi: "Tiếng Việt", id: "Bahasa Indonesia", he: "עברית", hi: "हिन्दी",
};

/* El código que se le pasa a Intl para nombrar el idioma: el chino que
   traducimos es el simplificado, y así lo dice («chino simplificado»). */
const CODIGO_INTL = { zh: "zh-Hans" };

/**
 * El nombre de un idioma en el idioma del PANEL («inglés», «chino
 * simplificado»), sacado del navegador: así sale bien en los ocho idiomas del
 * panel sin tener que mantener una tabla por cada uno.
 */
export function nombreEnElPanel(cod, locale) {
  try {
    const dn = new Intl.DisplayNames([String(locale || "es")], { type: "language" });
    return dn.of(CODIGO_INTL[cod] || cod) || "";
  } catch (e) {
    return "";
  }
}

export const nombreDeIdioma = (cod) => NOMBRE_IDIOMA[cod] || String(cod || "").toUpperCase();

function leerBool(clave) {
  try {
    return localStorage.getItem(clave) === "1";
  } catch (e) {
    return false;
  }
}

export default {
  data() {
    return {
      trad: {
        /** El interruptor. Vive en ESTE navegador: es una forma de trabajar de
            cada rato, no una preferencia que haya que guardar en la cuenta. */
        activo: leerBool(CLAVE_ACTIVO),
        /** En qué idioma escribe el huésped de esta conversación. */
        idiomaHuesped: "",
        /** El elegido a mano para contestar; vacío = el del huésped. */
        idiomaSalida: "",
        /** Traducción de cada mensaje que ha entrado, por id. */
        porMensaje: {},
        /** La previa de lo que se va a enviar. */
        previa: null,
        pidiendo: false,
        traduciendo: false,
        /** La conversación cuyo idioma está cargado, para no pedirlo dos veces. */
        reservaCargada: null,
      },
    };
  },

  computed: {
    /** El idioma en el que mira el anfitrión: el del panel. */
    tradMiIdioma() {
      const l = (this.$i18n && this.$i18n.locale) || "es";
      return String(l).slice(0, 2).toLowerCase();
    },
    tradMiIdiomaNombre() {
      return nombreDeIdioma(this.tradMiIdioma);
    },
    /** En el que saldrá de verdad lo que se escriba. */
    tradSalida() {
      /* Sin idioma del huésped (un chat sin reserva), el punto de partida es el
         mío: el selector enseña algo que existe y no se traduce nada hasta que
         se elija otro. */
      return this.trad.idiomaSalida || this.trad.idiomaHuesped || this.tradMiIdioma;
    },
    tradSalidaNombre() {
      return nombreDeIdioma(this.tradSalida);
    },
    /** Lo que ENTRA hay que traerlo a mi idioma. */
    tradHayQueLeer() {
      return !!this.trad.idiomaHuesped && this.trad.idiomaHuesped !== this.tradMiIdioma;
    },
    /** Lo que SALE hay que llevarlo al idioma de salida. */
    tradHayQueEnviar() {
      return !!this.tradSalida && this.tradSalida !== this.tradMiIdioma;
    },
    /** Las opciones del selector, con el idioma del huésped el primero. */
    tradOpciones() {
      const suyo = this.trad.idiomaHuesped;
      const locale = (this.$i18n && this.$i18n.locale) || "es";
      const vistos = {};
      const lista = [];
      /* Cada idioma en el suyo («English») y al lado en el del panel
         («inglés»), para que se reconozca aunque no se lea esa escritura. */
      const rotulo = (cod) => {
        const propio = nombreDeIdioma(cod);
        const delPanel = nombreEnElPanel(cod, locale);
        return delPanel && delPanel.toLowerCase() !== propio.toLowerCase()
          ? propio + " · " + delPanel
          : propio;
      };
      const meter = (cod, etiqueta) => {
        if (!cod || vistos[cod]) return;
        vistos[cod] = true;
        lista.push({ cod, etiqueta });
      };
      if (suyo) meter(suyo, rotulo(suyo) + " — " + this.$t("mensajesChat.translate.theirs"));
      IDIOMAS_SALIDA.forEach((c) => meter(c, rotulo(c)));
      return lista;
    },
  },

  methods: {
    /** Enciende o apaga el traductor de esta y de las siguientes conversaciones. */
    tradAlternar() {
      this.trad.activo = !this.trad.activo;
      try {
        localStorage.setItem(CLAVE_ACTIVO, this.trad.activo ? "1" : "0");
      } catch (e) {
        /* en privado no se recuerda, pero el chat funciona igual */
      }
      if (!this.trad.activo) this.trad.previa = null;
      return this.trad.activo;
    },

    /** El idioma elegido a mano para contestar a ESTE huésped. */
    tradElegirSalida(cod, reservaId) {
      const limpio = /^[a-z]{2}$/.test(String(cod || "")) ? cod : "";
      this.trad.idiomaSalida = limpio && limpio !== this.trad.idiomaHuesped ? limpio : "";
      try {
        const clave = CLAVE_SALIDA + (reservaId || "suelta");
        if (this.trad.idiomaSalida) localStorage.setItem(clave, this.trad.idiomaSalida);
        else localStorage.removeItem(clave);
      } catch (e) {
        /* ídem */
      }
      /* Lo previsualizado iba a otro idioma: ya no vale. */
      this.trad.previa = null;
    },

    /**
     * En qué idioma habla el huésped de esta reserva. Se pregunta al servidor,
     * que usa el MISMO criterio que el agente: así el anfitrión y el agente no
     * acaban escribiéndole en idiomas distintos.
     */
    async tradCargarIdioma(reservaId) {
      this.trad.previa = null;
      this.trad.porMensaje = {};
      this.trad.idiomaSalida = "";
      this.trad.idiomaHuesped = "";
      this.trad.reservaCargada = reservaId || null;

      try {
        const clave = CLAVE_SALIDA + (reservaId || "suelta");
        const guardada = localStorage.getItem(clave);
        if (guardada && /^[a-z]{2}$/.test(guardada)) this.trad.idiomaSalida = guardada;
      } catch (e) {
        /* sin almacenamiento: se empieza con el detectado */
      }

      if (!reservaId) return;

      try {
        const { data } = await axios.get("/translate/idioma-del-huesped", {
          params: { reserva_id: reservaId },
        });
        if (data && data.ok) this.trad.idiomaHuesped = data.codigo || "";
      } catch (e) {
        /* Si no se sabe su idioma, el traductor se queda callado: es mejor que
           inventarse uno y traducirlo todo al revés. */
      }
    },

    /**
     * Trae a mi idioma lo que ha entrado y todavía no está traducido.
     *
     * `sacar` convierte cada mensaje de ESA pantalla en { id, texto } y
     * devuelve null para los que no hay que traducir (los míos, los vacíos).
     */
    async tradTraerLoQueFalta(mensajes, sacar) {
      if (!this.trad.activo || !this.tradHayQueLeer || this.trad.pidiendo) return;

      const faltan = [];
      (mensajes || []).forEach((m) => {
        const uno = sacar(m);
        if (!uno || !uno.texto || this.trad.porMensaje[uno.id]) return;
        faltan.push(uno);
      });
      if (!faltan.length) return;

      /* Los últimos: una conversación larga no se traduce entera para leer lo
         de hoy, y el servidor tiene su tope por viaje. */
      const lote = faltan.slice(-30);
      this.trad.pidiendo = true;
      try {
        const { data } = await axios.post("/translate/chat", {
          textos: lote,
          target_lang: this.tradMiIdioma,
        });
        if (data && data.ok) {
          const nuevas = Object.assign({}, this.trad.porMensaje);
          (data.data || []).forEach((m) => {
            if (m && m.texto) nuevas[m.id] = m.texto;
          });
          this.trad.porMensaje = nuevas;
        }
      } catch (e) {
        /* Sin traducción se lee el original: molesto, pero nada se rompe. */
      } finally {
        this.trad.pidiendo = false;
      }
    },

    /** El nombre de un idioma, para la barra. */
    tradNombreDe(cod) {
      return nombreDeIdioma(cod);
    },

    /** La traducción de un mensaje, si ya la tenemos. */
    tradDe(id) {
      return this.trad.porMensaje[id] || null;
    },

    /** Lleva lo escrito al idioma de salida y lo deja a la vista, sin mandarlo. */
    async tradPrevisualizar(texto, reservaId) {
      const t = String(texto || "").trim();
      if (!t || this.trad.traduciendo) return null;

      this.trad.traduciendo = true;
      try {
        const cuerpo = { textos: [{ id: "previa", texto: t }] };
        if (this.trad.idiomaSalida) cuerpo.target_lang = this.trad.idiomaSalida;
        else {
          cuerpo.para = "huesped";
          cuerpo.reserva_id = reservaId;
        }
        const { data } = await axios.post("/translate/chat", cuerpo);
        const salida = data && data.ok && data.data && data.data[0] ? data.data[0].texto : "";
        if (!salida) return null;
        this.trad.previa = {
          texto: salida,
          idioma: data.idioma,
          nombre: data.idioma_nombre || nombreDeIdioma(data.idioma),
          origen: t,
        };
        return this.trad.previa;
      } catch (e) {
        return null;
      } finally {
        this.trad.traduciendo = false;
      }
    },

    tradDescartarPrevia() {
      this.trad.previa = null;
    },

    /**
     * Qué hacer con el Enter.
     *
     * Devuelve true si el traductor se ha encargado (ha pedido la previa o ha
     * mandado la traducción) y false si la pantalla debe enviar como siempre.
     */
    tradAlPulsarEnter(texto, reservaId, enviar) {
      if (this.trad.previa) {
        const traducido = this.trad.previa.texto;
        this.trad.previa = null;
        enviar(traducido);
        return true;
      }

      /* Encendido, el primer Enter enseña SIEMPRE la previa, como en el buzón
         del manager: quien lo enciende quiere ver lo que sale antes de que
         salga, aunque crea que el huésped habla su idioma. */
      if (this.trad.activo && String(texto || "").trim()) {
        this.tradPrevisualizar(texto, reservaId);
        return true;
      }

      return false;
    },

    /** Si se sigue escribiendo, la previa de antes ya no vale. */
    tradAlEscribir(texto) {
      if (this.trad.previa && String(texto || "").trim() !== this.trad.previa.origen) {
        this.trad.previa = null;
      }
    },
  },
};
